import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, Power } from "lucide-react";

const MOTIVOS = [
  { value: "apto", label: "🏆 Apto" },
  { value: "no_interesado", label: "🔴 No interesado" },
  { value: "baja", label: "⚫ Baja" },
  { value: "traslado", label: "🔷 Traslado" },
  { value: "archivo_historico", label: "📦 Archivo histórico" },
];

const CLOSURE_REASONS = [
  { value: "", label: "— Sin especificar —" },
  { value: "precio", label: "Precio" },
  { value: "otra_autoescuela", label: "Otra autoescuela" },
  { value: "falta_tiempo", label: "Falta de tiempo" },
  { value: "no_responde", label: "No responde" },
  { value: "cambio_domicilio", label: "Cambio de domicilio" },
  { value: "otro", label: "Otro" },
];

export default function DeactivateStudentDialog({ student, open, onClose, onDone, mode = "deactivate" }) {
  const isEdit = mode === "edit";
  const [motivo, setMotivo] = useState("");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setMotivo(isEdit ? (student?.lifecycle_stage || "") : "");
      setReason("");
      setNotes("");
    }
  }, [open, isEdit, student]);

  const submit = async () => {
    if (!motivo) return toast.error("Selecciona el motivo");
    setSaving(true);
    try {
      const body = {
        motivo,
        closure_reason: motivo === "no_interesado" ? (reason || null) : null,
        closure_notes: motivo === "no_interesado" ? notes : "",
      };
      if (isEdit) {
        await api.patch(`/students/${student.id}/closure-motivo`, body);
        toast.success("Motivo de cierre corregido");
      } else {
        await api.post(`/students/${student.id}/toggle`, body);
        toast.success("Alumno desactivado");
      }
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="deactivate-student-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Power className="h-5 w-5 text-neutral-600" /> {isEdit ? "Corregir motivo de cierre" : "Desactivar alumno"}
          </DialogTitle>
          <DialogDescription>
            {student?.name} · {isEdit
              ? "corrección de Matriz: el nuevo motivo pasará a ser el estado principal (auditado)."
              : "indica el motivo: pasará a ser su estado principal visible."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-neutral-500 font-bold">Motivo *</label>
            <select
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              data-testid="deactivate-motivo"
              className="mt-1 w-full py-2 px-3 rounded-lg border border-neutral-300 bg-white text-sm focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none"
            >
              <option value="">— Selecciona —</option>
              {MOTIVOS.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>

          {motivo === "no_interesado" && (
            <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-3 space-y-2">
              <div className="text-[11px] uppercase tracking-wider text-neutral-500 font-bold">Motivo de cierre (opcional)</div>
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                data-testid="deactivate-reason"
                className="w-full py-2 px-3 rounded-lg border border-neutral-300 bg-white text-sm focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none"
              >
                {CLOSURE_REASONS.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
              <div>
                <label className="text-[10px] text-neutral-500">Observaciones (para informes comerciales)</label>
                <textarea
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  data-testid="deactivate-notes"
                  className="w-full py-1.5 px-2 rounded-md border border-neutral-300 text-sm"
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} disabled={saving}
            className="btn-ghost border border-neutral-200"
            data-testid="deactivate-cancel">
            Cancelar
          </button>
          <button type="button" onClick={submit} disabled={saving || !motivo}
            data-testid="deactivate-confirm"
            className="btn-primary disabled:opacity-50">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Power className="h-4 w-4" />}
            {isEdit ? "Guardar corrección" : "Desactivar"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
